// parseBankStatement.js — lee el extracto de movimientos del Santander (el PDF
// "Movimientos de Cuenta" que descarga el banco) y lo convierte en objetos
// { fecha, concepto, importe, saldo } iguales a los que ya produce el CSV.
//
// El texto de un PDF, una vez extraído, no llega como una tabla — llega como
// líneas sueltas en el orden en que aparecen en el documento. En este extracto
// cada movimiento se repite siempre con la misma forma:
//
//   F. Valor
//   <concepto, 1 o más líneas>
//   <importe> EUR <saldo> EUR
//   <fecha valor>
//   <fecha operación>
//
// "F. Valor" es un separador fiable entre movimientos (aparece también una
// vez suelto, justo tras la cabecera de columnas, antes del primer
// movimiento — ese bloque se descarta porque no tiene línea de importe).
//
// Las dos fechas casi siempre coinciden, pero no siempre (p.ej. "Entrega de
// documentos para su compensación" liquida días después de la operación) —
// comparado contra el CSV real del mismo extracto, la que aparece PRIMERO en
// el texto es la fecha valor y la que aparece DESPUÉS es la fecha de
// operación (la fecha "de verdad" del movimiento) — por eso aquí se coge la
// ÚLTIMA fecha del bloque, no la primera.
'use strict';

const IMPORTE_RE = /^(-?[\d.]+,\d{2})\s*EUR\s+([\d.]+,\d{2})\s*EUR$/;
const FECHA_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

// Líneas de cabecera/pie de página que no forman parte de ningún movimiento —
// se descartan antes de trocear en bloques para que no se cuelen dentro de un
// concepto si el movimiento cae justo en un salto de página.
const NOISE_LINE_RES = [
  /^Movimientos desde el /i,
  /^Fecha Concepto Importe Saldo$/i,
  /^\d{1,4}$/, // número de página suelto
  /^Titular$/i, /^Saldo disponible$/i, /^Cuenta$/i, /^Saldo real$/i,
  /^Retenciones$/i, /^Saldo consolidado$/i,
];

function parseImporteEs(s) {
  const trimmed = s.trim();
  const neg = trimmed.startsWith('-');
  const clean = trimmed.replace(/^-/, '').replace(/\./g, '').replace(',', '.');
  const n = parseFloat(clean);
  if (isNaN(n)) return null;
  return neg ? -n : n;
}

function isNoiseLine(line) {
  return NOISE_LINE_RES.some(re => re.test(line));
}

// Extrae los movimientos de un extracto Santander a partir de su texto plano
// (ya sea el texto de las páginas de un PDF, o el texto pegado a mano desde un
// visor de PDF — mismo parser para los dos, ver BancosView.js).
function parseSantanderStatementText(rawText) {
  const lines = String(rawText || '')
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean)
    .filter(l => !isNoiseLine(l));

  // Trocear en bloques usando "F. Valor" como separador.
  const blocks = [];
  let current = [];
  for (const line of lines) {
    if (line === 'F. Valor') {
      if (current.length) blocks.push(current);
      current = [];
    } else {
      current.push(line);
    }
  }
  if (current.length) blocks.push(current);

  const movimientos = [];
  for (const block of blocks) {
    const importeIdx = block.findIndex(l => IMPORTE_RE.test(l));
    if (importeIdx === -1) continue; // sin importe reconocible → no es un movimiento real

    const importeMatch = block[importeIdx].match(IMPORTE_RE);
    const fechasDelBloque = block.slice(importeIdx + 1).filter(l => FECHA_RE.test(l));
    const fechaLine = fechasDelBloque[fechasDelBloque.length - 1]; // fecha de operación, no fecha valor
    if (!fechaLine) continue;

    const conceptoLines = block.slice(0, importeIdx);
    const concepto = conceptoLines.join(' ').replace(/\s+/g, ' ').trim();
    const [, dd, mm, yyyy] = fechaLine.match(FECHA_RE);
    const importe = parseImporteEs(importeMatch[1]);
    const saldo = parseImporteEs(importeMatch[2]);
    if (importe === null || !concepto) continue;

    movimientos.push({ fecha: `${yyyy}-${mm}-${dd}`, concepto, importe, saldo });
  }

  return movimientos;
}

// ── CSV del Santander ("Movimientos de Cuenta" exportado en CSV) ──────────────
// Mucho más fiable que el PDF: es una tabla real, sin ambigüedad de orden de
// texto. El fichero trae 6-7 líneas de ficha de cuenta antes de la tabla, así
// que la fila de cabecera se busca por contenido ("Fecha Operación"), no por
// posición — y se parsea respetando comillas (el concepto casi siempre trae
// comas dentro, p.ej. "Recibo Eurofon, S.l., Concepto:").
function splitCsvLine(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = false;
      } else cur += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

function parseSantanderCSV(rawText) {
  const lines = String(rawText || '').split(/\r?\n/).filter(l => l.trim());
  // "Fecha Operaci" a secas (sin exigir la "ó"/"o" final) — si el archivo se
  // lee con una codificación distinta a la suya (p.ej. ISO-8859-1 leído como
  // UTF-8), la letra acentuada puede llegar corrompida como un carácter de
  // reemplazo; el prefijo sin acento sigue siendo inconfundible igualmente.
  const headerIdx = lines.findIndex(l => /Fecha Operaci/i.test(l));
  if (headerIdx === -1) return [];

  const headers = splitCsvLine(lines[headerIdx]).map(h => h.trim().toLowerCase());
  const col = {
    fecha:    headers.findIndex(h => h.includes('fecha operaci')),
    concepto: headers.findIndex(h => h === 'concepto'),
    importe:  headers.findIndex(h => h === 'importe'),
    saldo:    headers.findIndex(h => h === 'saldo'),
  };
  if (col.fecha === -1 || col.concepto === -1 || col.importe === -1) return [];

  const movimientos = [];
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i]);
    const fechaMatch = (cols[col.fecha] || '').trim().match(FECHA_RE);
    if (!fechaMatch) continue;
    const importe = parseImporteEs(cols[col.importe] || '');
    if (importe === null) continue;
    const concepto = (cols[col.concepto] || '').trim();
    if (!concepto) continue;
    const [, dd, mm, yyyy] = fechaMatch;
    const saldo = col.saldo >= 0 ? parseImporteEs(cols[col.saldo] || '') : null;

    movimientos.push({ fecha: `${yyyy}-${mm}-${dd}`, concepto, importe, saldo });
  }
  return movimientos;
}

// ── Categorización automática ─────────────────────────────────────────────────
// Basada en los patrones reales de concepto que usa StelOrder/el banco para
// nóminas, Seguridad Social, TPV, etc. — no es exhaustiva, pero cubre el
// grueso del movimiento habitual de la cuenta y deja el resto en "otros".
const CATEGORIAS = {
  nomina:              { label: 'Nóminas',                 tipo: 'gasto',   slot: 1 },
  seguridad_social:    { label: 'Seguridad Social',         tipo: 'gasto',   slot: 2 },
  impuestos:           { label: 'Impuestos (IVA/IRPF)',     tipo: 'gasto',   slot: 3 },
  proveedor:           { label: 'Proveedores y recibos',    tipo: 'gasto',   slot: 4 },
  comision_bancaria:   { label: 'Comisiones bancarias',     tipo: 'gasto',   slot: 5 },
  tarjeta:             { label: 'Gastos con tarjeta',       tipo: 'gasto',   slot: 6 },
  liquidacion_tarjeta: { label: 'Liquidaciones datáfono',   tipo: 'ingreso', slot: 1 },
  cliente:             { label: 'Ingresos de clientes',     tipo: 'ingreso', slot: 2 },
  efectivo:            { label: 'Ingresos en efectivo',     tipo: 'ingreso', slot: 3 },
  traspaso_interno:    { label: 'Traspaso entre cuentas propias', tipo: 'interno', slot: 7 },
  otros_gastos:        { label: 'Otros gastos',             tipo: 'gasto',   slot: 8 },
  otros_ingresos:      { label: 'Otros ingresos',           tipo: 'ingreso', slot: 4 },
};

// Nombre del titular tal y como aparece en los conceptos "a favor de sí misma"
// — traspasos a esta persona son movimiento interno, no gasto ni ingreso real
// del negocio. Configurable por si cambia el titular de la cuenta.
const TITULAR_RE = /maria reyes perez gamez/i;

function categorizeMovimiento(concepto, importe) {
  const c = (concepto || '').toLowerCase();

  let categoria;
  if (TITULAR_RE.test(c) && /traspaso|pagos proveedores/i.test(c)) {
    categoria = 'traspaso_interno';
  } else if (/nomina|paga extra/i.test(c)) {
    categoria = 'nomina';
  } else if (/tgss|seguridad social|r\.?e\.?autonomos|regimen general/i.test(c)) {
    categoria = 'seguridad_social';
  } else if (/iva.*autoliquidacion|i\.?r\.?p\.?f\.?|retenciones e ing|hacienda|agencia tributaria|^domiciliacion impuesto/i.test(c)) {
    categoria = 'impuestos';
  } else if (/liquidacion efectuada/i.test(c)) {
    categoria = 'liquidacion_tarjeta';
  } else if (/tpv.*tasa de descuento|comisi[oó]n/i.test(c)) {
    categoria = 'comision_bancaria';
  } else if (/pago movil en|tarj\.\s*:\s*\*/i.test(c)) {
    categoria = 'tarjeta';
  } else if (/\brecibo\b/i.test(c)) {
    categoria = 'proveedor';
  } else if (/^entrega de documentos para su compensacion/i.test(c)) {
    categoria = 'cliente';
  } else if (/^ingreso an[oó]nimo en cajero/i.test(c)) {
    categoria = 'efectivo';
  } else if (/^transferencia a favor de/i.test(c)) {
    categoria = 'otros_gastos';
  } else if (/^transferencia/i.test(c)) {
    categoria = 'cliente';
  } else {
    categoria = importe >= 0 ? 'otros_ingresos' : 'otros_gastos';
  }

  return { categoria, ...CATEGORIAS[categoria] };
}

module.exports = { parseSantanderStatementText, parseSantanderCSV, categorizeMovimiento, parseImporteEs, CATEGORIAS };
